import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BanknotesIcon, 
  ReceiptPercentIcon, 
  DocumentTextIcon, 
  CalendarDaysIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import SettingsInfoNotice from './SettingsInfoNotice';

const BillingSettings = ({ settings, onSettingsChange, onHasChanges }) => {
  const [billingSettings, setBillingSettings] = useState({
    taxRate: 19,
    invoicePrefix: 'FAC',
    paymentTerms: 30,
    lateFee: 0
  });

  useEffect(() => {
    if (settings?.billing) {
      setBillingSettings(settings.billing);
    }
  }, [settings]);

  const handleChange = (field, value) => {
    const newBillingSettings = {
      ...billingSettings,
      [field]: value
    };
    setBillingSettings(newBillingSettings);
    onSettingsChange({ ...settings, billing: newBillingSettings });
    onHasChanges(true);
  };

  const calculateTaxAmount = (amount) => {
    return (amount * billingSettings.taxRate / 100).toFixed(2);
  };

  const calculateTotal = (amount) => {
    return (amount + parseFloat(calculateTaxAmount(amount))).toFixed(2);
  };

  return (
    <div className="space-y-6">
      <SettingsInfoNotice variant="info" title="Utilisation dans l'application">
        <p>
          Le préfixe de facture et le taux de TVA sont appliqués sur la page Paiements (numéros de facture et reçus).
          Les conditions de paiement et frais de retard sont enregistrés pour une utilisation future sur les relances.
        </p>
      </SettingsInfoNotice>

      <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
        {/* Taux de TVA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="space-y-4"
        >
          <div className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
            <div className="flex items-center gap-3 mb-4">
              <ReceiptPercentIcon className="h-5 w-5 text-blue-400" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-50">Taux de TVA</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={billingSettings.taxRate}
                  onChange={(e) => handleChange('taxRate', parseFloat(e.target.value) || 0)}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-20 bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-3 py-2 text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-slate-700 dark:text-slate-300">%</span>
              </div>
              
              <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-3">
                <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">Exemple de calcul :</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-700 dark:text-slate-300">Montant HT :</span>
                    <span className="text-slate-900 dark:text-slate-50">1000,00 DA</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-700 dark:text-slate-300">TVA ({billingSettings.taxRate}%) :</span>
                    <span className="text-slate-900 dark:text-slate-50">{calculateTaxAmount(1000)} DA</span>
                  </div>
                  <div className="flex justify-between font-semibold border-t border-slate-200 dark:border-slate-600 pt-1">
                    <span className="text-slate-900 dark:text-slate-50">Total TTC :</span>
                    <span className="text-slate-900 dark:text-slate-50">{calculateTotal(1000)} DA</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Préfixe des factures */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          <div className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
            <div className="flex items-center gap-3 mb-4">
              <DocumentTextIcon className="h-5 w-5 text-purple-400" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-50">Préfixe des factures</h4>
            </div>
            
            <div className="space-y-3">
              <input
                type="text"
                value={billingSettings.invoicePrefix}
                onChange={(e) => handleChange('invoicePrefix', e.target.value)}
                maxLength="10"
                className="w-full bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-4 py-3 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="INV"
              />
              
              <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-3">
                <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">Aperçu des numéros de facture :</p>
                <div className="space-y-1 text-sm">
                  <div className="text-slate-700 dark:text-slate-300">{billingSettings.invoicePrefix}-2024-001</div>
                  <div className="text-slate-700 dark:text-slate-300">{billingSettings.invoicePrefix}-2024-002</div>
                  <div className="text-slate-700 dark:text-slate-300">{billingSettings.invoicePrefix}-2024-003</div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Conditions de paiement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-4"
        >
          <div className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
            <div className="flex items-center gap-3 mb-4">
              <CalendarDaysIcon className="h-5 w-5 text-orange-400" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-50">Conditions de paiement</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={billingSettings.paymentTerms}
                  onChange={(e) => handleChange('paymentTerms', parseInt(e.target.value) || 0)}
                  min="0"
                  max="365"
                  className="w-20 bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-3 py-2 text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-slate-700 dark:text-slate-300">jours</span>
              </div>
              
              <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-3">
                <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">Conditions courantes :</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <button
                    onClick={() => handleChange('paymentTerms', 0)}
                    className={`p-2 rounded-lg transition-colors ${
                      billingSettings.paymentTerms === 0 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-slate-100/80 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                    }`}
                  >
                    Comptant
                  </button>
                  <button
                    onClick={() => handleChange('paymentTerms', 15)}
                    className={`p-2 rounded-lg transition-colors ${
                      billingSettings.paymentTerms === 15 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-slate-100/80 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                    }`}
                  >
                    15 jours
                  </button>
                  <button
                    onClick={() => handleChange('paymentTerms', 30)}
                    className={`p-2 rounded-lg transition-colors ${
                      billingSettings.paymentTerms === 30 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-slate-100/80 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                    }`}
                  >
                    30 jours
                  </button>
                  <button
                    onClick={() => handleChange('paymentTerms', 60)}
                    className={`p-2 rounded-lg transition-colors ${
                      billingSettings.paymentTerms === 60 
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'bg-slate-100/80 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/80'
                    }`}
                  >
                    60 jours
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Frais de retard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-4"
        >
          <div className="rounded-2xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              <h4 className="font-semibold text-slate-900 dark:text-slate-50">Frais de retard</h4>
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={billingSettings.lateFee}
                  onChange={(e) => handleChange('lateFee', parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.1"
                  className="w-24 bg-slate-50 dark:bg-slate-800/80 backdrop-blur-lg rounded-xl px-3 py-2 text-slate-900 dark:text-slate-50 border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center"
                />
                <span className="text-slate-700 dark:text-slate-300">DA par jour</span>
              </div>
              
              <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-3">
                <p className="text-slate-600 dark:text-slate-400 text-sm mb-2">Exemple de calcul :</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-700 dark:text-slate-300">Retard de 10 jours :</span>
                    <span className="text-slate-900 dark:text-slate-50">{(billingSettings.lateFee * 10).toFixed(2)} DA</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-700 dark:text-slate-300">Retard de 30 jours :</span>
                    <span className="text-slate-900 dark:text-slate-50">{(billingSettings.lateFee * 30).toFixed(2)} DA</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Résumé des paramètres */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-slate-100/80 dark:bg-slate-800/40 backdrop-blur-lg rounded-2xl p-6 border border-slate-200 dark:border-slate-600"
      >
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-4">Résumé des paramètres de facturation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 text-center">
            <ReceiptPercentIcon className="h-6 w-6 text-blue-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">{billingSettings.taxRate}%</div>
            <div className="text-slate-600 dark:text-slate-400 text-sm">Taux de TVA</div>
          </div>
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 text-center">
            <DocumentTextIcon className="h-6 w-6 text-purple-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">{billingSettings.invoicePrefix}</div>
            <div className="text-slate-600 dark:text-slate-400 text-sm">Préfixe facture</div>
          </div>
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 text-center">
            <CalendarDaysIcon className="h-6 w-6 text-orange-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">{billingSettings.paymentTerms}</div>
            <div className="text-slate-600 dark:text-slate-400 text-sm">Jours de paiement</div>
          </div>
          <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-4 text-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mx-auto mb-2" />
            <div className="text-2xl font-bold text-slate-900 dark:text-slate-50">{billingSettings.lateFee}</div>
            <div className="text-slate-600 dark:text-slate-400 text-sm">Frais/jour (DA)</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BillingSettings; 